import { ContentState } from "draft-js";
import type { NextPageWithLayout } from "next";
import {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Resizable, ResizeCallbackData } from "react-resizable";

import { accountApi } from "@/apis/accounts/accountApi";
import { Button } from "@/components/atoms/buttons/Button";
import DraftEditor from "@/components/editors/draft-js-rte/DraftEditor";
import { PageHeader } from "@/components/organisms/header/PageHeader";
import { LayoutTemplate } from "@/components/templates/layout";
import { useSystemMessage } from "@/hooks/systemMessage";
import { TextBaseStyle, ToolbarSet } from "@/models/editors/editor";
import { useLoading } from "@/states/loading/operations";
import {
  convertFromRawRecursively,
  convertToRawRecursively,
} from "@/utils/convert-page-json";
import { createEmptyContent } from "@/utils/draft";

const HEIGHT_DEFAULT = 230;

const AccountTermPage: NextPageWithLayout = () => {
  const withLoading = useLoading();
  const systemMessage = useSystemMessage();

  const [contentState, setContentState] = useState<ContentState>();
  const [textAreaStyle, setTextAreaStyle] = useState<TextBaseStyle>({
    height: HEIGHT_DEFAULT,
  });

  const handleSubmitTerm = async () => {
    if (contentState) {
      const draftContentState = convertToRawRecursively(contentState);
      await withLoading(async () => {
        await accountApi.postAccountTerm({
          postAccountTermRequest: {
            term_json: { content: draftContentState, textAreaStyle },
          },
        });
        systemMessage.success("保存に成功しました");
      });
    }
  };

  const getAccountTerm = async () => {
    const res = await accountApi.getAccountTerm();
    const termData = res.data?.term_json;
    if (termData && termData?.content) {
      const plainContent = termData.content;
      const textStyle = termData.textAreaStyle;

      const draftEditorContent = convertFromRawRecursively(plainContent);

      const content = draftEditorContent.getCurrentContent();
      setContentState(content);
      setTextAreaStyle({ ...textAreaStyle, ...textStyle });
      return;
    }
    setContentState(createEmptyContent());
  };

  useEffect(() => {
    void getAccountTerm();
  }, []);

  const isEmptyAccountTerm = useMemo(() => {
    if (contentState) {
      const plainText = contentState.getPlainText().trim();
      return !plainText;
    }
    return false;
  }, [contentState]);

  const onResize = (_: SyntheticEvent, { size }: ResizeCallbackData) => {
    setTextAreaStyle;
    setTextAreaStyle({ ...textAreaStyle, height: size.height });
  };

  return (
    <article>
      <div className="md:mt-6 mb-10">
        <PageHeader title="利用規約"></PageHeader>
      </div>

      <div>
        <div className="mb-8 space-y-4 rounded-[4px] shadow-sm	 p-[10px] border border-gray-100">
          {contentState && (
            <Resizable
              height={textAreaStyle.height ?? HEIGHT_DEFAULT}
              width={0}
              onResize={onResize}
            >
              <div>
                <DraftEditor
                  className={`box overflow-auto box-content `}
                  useTools={ToolbarSet.TEXT}
                  contentState={contentState}
                  handleChange={setContentState}
                  textBaseStyle={textAreaStyle}
                  handleBaseStyleChange={setTextAreaStyle}
                />
              </div>
            </Resizable>
          )}
        </div>
      </div>

      <div className="flex">
        <Button
          label={"変更を保存する"}
          className="ml-auto w-[160px]"
          onClick={handleSubmitTerm}
          disabled={isEmptyAccountTerm}
        />
      </div>
    </article>
  );
};

AccountTermPage.getLayout = function getLayout(children: ReactElement) {
  return <LayoutTemplate sideMenuClassname="!z-0">{children}</LayoutTemplate>;
};

export default AccountTermPage;
